import { useEffect, useMemo } from "react"
import { useLocation, useSearchParams } from "react-router"

import { ASC, DESC } from "@/utils/constants"
import {
  stageSearchParamUpdates,
  useSearchParamStagingScope,
} from "@/utils/url"

import type { GridSortModel, GridValidRowModel } from "@mui/x-data-grid-pro"

import { getSortModelSignature } from "./sort-model-utils"
import type { UseGridSortModelProps } from "./use-grid-sort-model.types"

interface ResolveGridSortModelParams {
  defaultSortModel: GridSortModel
  fieldSearchParam: string | null
  savedSortModel: GridSortModel
  sortSearchParam: string | null
  validColumnFields: readonly string[]
}

const computeValidSortModel = (
  sortModel: GridSortModel,
  validColumnFields: readonly string[]
): GridSortModel => {
  const validColumnFieldSet = new Set(validColumnFields)

  return sortModel.filter((sort) => validColumnFieldSet.has(sort.field))
}

const resolveGridSortModel = ({
  defaultSortModel,
  fieldSearchParam,
  savedSortModel,
  sortSearchParam,
  validColumnFields,
}: ResolveGridSortModelParams): GridSortModel => {
  let newSortModel: GridSortModel

  if (fieldSearchParam && sortSearchParam) {
    const urlSortModel: GridSortModel = [
      {
        field: fieldSearchParam,
        sort: sortSearchParam === ASC ? ASC : DESC,
      },
    ]

    newSortModel = computeValidSortModel(urlSortModel, validColumnFields)
  } else {
    newSortModel = computeValidSortModel(savedSortModel, validColumnFields)
  }

  if (newSortModel.length === 0) {
    newSortModel = defaultSortModel
  }

  return newSortModel
}

/**
 * Returns the new sort model by taking into account removed columns
 * Only supports single sort at this time
 *
 * @returns The new sort model
 * @example
 * const sortModel = useGridSortModel({
 *   defaultSortModel,
 *   savedSortModel,
 *   validColumns,
 * })
 */
export const useGridSortModel = <RowT extends GridValidRowModel>({
  defaultSortModel,
  savedSortModel,
  validColumns,
}: UseGridSortModelProps<RowT>): GridSortModel => {
  const location = useLocation()
  const searchParamStagingScope = useSearchParamStagingScope()
  const [searchParams, setSearchParams] = useSearchParams()

  const fieldSearchParam: string | null = searchParams.get("orderBy")
  const sortSearchParam: string | null = searchParams.get("order")

  const validColumnFieldsSignature = useMemo(
    () => validColumns.map((column) => column.field).join("\0"),
    [validColumns]
  )

  const validColumnFields = useMemo(
    () =>
      validColumnFieldsSignature.length > 0
        ? validColumnFieldsSignature.split("\0")
        : [],
    [validColumnFieldsSignature]
  )

  const savedSortModelSignature = getSortModelSignature(savedSortModel)

  // Depend on value signatures, not prop references, so sortModel stays referentially
  // stable across re-renders (e.g. infinite scroll) and MUI does not reset scroll.
  const sortModel: GridSortModel = useMemo(
    () =>
      resolveGridSortModel({
        defaultSortModel,
        fieldSearchParam,
        savedSortModel,
        sortSearchParam,
        validColumnFields,
      }),
    // eslint-disable-next-line @eslint-react/exhaustive-deps, react-hooks/exhaustive-deps -- stable identity deps
    [
      defaultSortModel,
      fieldSearchParam,
      // savedSortModel is referentially unstable, causing infinite scroll to reset to the top
      // savedSortModel,
      savedSortModelSignature,
      sortSearchParam,
      // validColumns is referentially unstable, causing infinite scroll to reset to the top
      // validColumns,
      validColumnFieldsSignature,
    ]
  )

  // Add sort to url if not present
  useEffect(() => {
    if (!fieldSearchParam || !sortSearchParam) {
      const sortItem = sortModel[0]
      if (!sortItem) {
        return
      }

      stageSearchParamUpdates({
        committed: searchParams,
        locationKey: location.key,
        replace: true,
        scope: searchParamStagingScope,
        setSearchParams,
        updates: {
          orderBy: sortItem.field,
          order: sortItem.sort,
        },
      })
    }
  }, [
    fieldSearchParam,
    location.key,
    searchParamStagingScope,
    searchParams,
    setSearchParams,
    sortModel,
    sortSearchParam,
  ])

  return sortModel
}
